export const CORE_NETWORK_PERMISSIONS = {
  VIEW: "core_network.view",
  VIEW_DASHBOARD: "core_network.view_dashboard",
  VIEW_MEMBERS: "core_network.view_members",
  VIEW_MEMBER_DETAIL: "core_network.view_member_detail",
  MANAGE_MEMBERS: "core_network.manage_members",
  MANAGE_MEMBERSHIPS: "core_network.manage_memberships",
  MANAGE_CAPABILITIES: "core_network.manage_capabilities",
  MANAGE_CREDENTIALS: "core_network.manage_credentials",
  MANAGE_SETTINGS: "core_network.manage_settings",
  CREATE_REQUEST: "core_network.create_request",
  VIEW_REQUEST: "core_network.view_request",
  ASSIGN_REQUEST: "core_network.assign_request",
  GENERATE_MATCHES: "core_network.generate_matches",
  SEND_OFFERS: "core_network.send_offers",
  MESSAGE: "core_network.message",
  COMMENT: "core_network.comment",
  SUBMIT_RESPONSE: "core_network.submit_response",
  REVIEW_SUBMISSION: "core_network.review_submission",
  RATE_PROVIDER: "core_network.rate_provider",
  MANAGE_PAYMENT: "core_network.manage_payment",
  CANCEL_REQUEST: "core_network.cancel_request",
  REASSIGN_REQUEST: "core_network.reassign_request",
  VIEW_AUDIT_ACTIVITY: "core_network.view_audit_activity",
  MANAGE_NOTIFICATIONS: "core_network.manage_notifications",
  MANAGE_BADGES: "core_network.manage_badges",
  MANAGE_SUBSCRIPTIONS: "core_network.manage_subscriptions",
  SETTINGS: "core_network.settings",
};

const ALL_CORE_NETWORK_PERMISSIONS = Object.values(CORE_NETWORK_PERMISSIONS);

const ROLE_PERMISSION_MAP = {
  administrator: ALL_CORE_NETWORK_PERMISSIONS,
  admin: ALL_CORE_NETWORK_PERMISSIONS,
  "super-administrator": ALL_CORE_NETWORK_PERMISSIONS,
  "order-rep": [
    CORE_NETWORK_PERMISSIONS.VIEW_DASHBOARD,
    CORE_NETWORK_PERMISSIONS.VIEW_REQUEST,
    CORE_NETWORK_PERMISSIONS.VIEW_MEMBERS,
    CORE_NETWORK_PERMISSIONS.VIEW_MEMBER_DETAIL,
    CORE_NETWORK_PERMISSIONS.CREATE_REQUEST,
    CORE_NETWORK_PERMISSIONS.ASSIGN_REQUEST,
    CORE_NETWORK_PERMISSIONS.GENERATE_MATCHES,
    CORE_NETWORK_PERMISSIONS.SEND_OFFERS,
    CORE_NETWORK_PERMISSIONS.MESSAGE,
    CORE_NETWORK_PERMISSIONS.COMMENT,
    CORE_NETWORK_PERMISSIONS.REVIEW_SUBMISSION,
    CORE_NETWORK_PERMISSIONS.RATE_PROVIDER,
  ],
  "client-rep": [
    CORE_NETWORK_PERMISSIONS.VIEW_DASHBOARD,
    CORE_NETWORK_PERMISSIONS.VIEW_REQUEST,
    CORE_NETWORK_PERMISSIONS.VIEW_MEMBERS,
    CORE_NETWORK_PERMISSIONS.VIEW_MEMBER_DETAIL,
    CORE_NETWORK_PERMISSIONS.CREATE_REQUEST,
    CORE_NETWORK_PERMISSIONS.ASSIGN_REQUEST,
    CORE_NETWORK_PERMISSIONS.GENERATE_MATCHES,
    CORE_NETWORK_PERMISSIONS.SEND_OFFERS,
    CORE_NETWORK_PERMISSIONS.MESSAGE,
    CORE_NETWORK_PERMISSIONS.COMMENT,
    CORE_NETWORK_PERMISSIONS.REVIEW_SUBMISSION,
    CORE_NETWORK_PERMISSIONS.RATE_PROVIDER,
  ],
  "purchasing-rep": [
    CORE_NETWORK_PERMISSIONS.VIEW_DASHBOARD,
    CORE_NETWORK_PERMISSIONS.VIEW_REQUEST,
    CORE_NETWORK_PERMISSIONS.CREATE_REQUEST,
    CORE_NETWORK_PERMISSIONS.ASSIGN_REQUEST,
    CORE_NETWORK_PERMISSIONS.GENERATE_MATCHES,
    CORE_NETWORK_PERMISSIONS.SEND_OFFERS,
    CORE_NETWORK_PERMISSIONS.MESSAGE,
    CORE_NETWORK_PERMISSIONS.COMMENT,
    CORE_NETWORK_PERMISSIONS.REVIEW_SUBMISSION,
    CORE_NETWORK_PERMISSIONS.RATE_PROVIDER,
  ],
  "production-rep": [
    CORE_NETWORK_PERMISSIONS.VIEW_DASHBOARD,
    CORE_NETWORK_PERMISSIONS.VIEW_REQUEST,
    CORE_NETWORK_PERMISSIONS.CREATE_REQUEST,
    CORE_NETWORK_PERMISSIONS.GENERATE_MATCHES,
    CORE_NETWORK_PERMISSIONS.SEND_OFFERS,
    CORE_NETWORK_PERMISSIONS.MESSAGE,
    CORE_NETWORK_PERMISSIONS.COMMENT,
    CORE_NETWORK_PERMISSIONS.REVIEW_SUBMISSION,
    CORE_NETWORK_PERMISSIONS.RATE_PROVIDER,
  ],
  "finance-rep": [
    CORE_NETWORK_PERMISSIONS.VIEW_REQUEST,
    CORE_NETWORK_PERMISSIONS.MANAGE_PAYMENT,
    CORE_NETWORK_PERMISSIONS.MESSAGE,
    CORE_NETWORK_PERMISSIONS.COMMENT,
  ],
};

const LEGACY_PERMISSION_ALIASES = {
  [CORE_NETWORK_PERMISSIONS.VIEW]: [
    CORE_NETWORK_PERMISSIONS.VIEW_DASHBOARD,
    CORE_NETWORK_PERMISSIONS.VIEW_REQUEST,
    CORE_NETWORK_PERMISSIONS.VIEW_MEMBERS,
    CORE_NETWORK_PERMISSIONS.VIEW_MEMBER_DETAIL,
  ],
  [CORE_NETWORK_PERMISSIONS.SETTINGS]: [CORE_NETWORK_PERMISSIONS.MANAGE_SETTINGS],
};

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}

function collectPermissionStrings(value, permissions = new Set()) {
  if (!value) return permissions;

  if (Array.isArray(value)) {
    value.forEach((entry) => collectPermissionStrings(entry, permissions));
    return permissions;
  }

  if (typeof value === "string") {
    value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean)
      .forEach((entry) => permissions.add(entry));
    return permissions;
  }

  if (typeof value === "object") {
    Object.entries(value).forEach(([key, nested]) => {
      if (nested === true) permissions.add(String(key));
      collectPermissionStrings(nested, permissions);
    });
  }

  return permissions;
}

function getExplicitPermissions(authState = {}) {
  const permissions = new Set();
  [
    authState?.permissions,
    authState?.coreNetworkPermissions,
    authState?.core_network_permissions,
    authState?.claims?.permissions,
    authState?.claims?.coreNetworkPermissions,
    authState?.coreNetworkAccessProfile?.permissions,
  ].forEach((entry) => collectPermissionStrings(entry, permissions));

  permissions.forEach((permission) => {
    (LEGACY_PERMISSION_ALIASES[permission] || []).forEach((alias) => permissions.add(alias));
  });

  return permissions;
}

function getRolePermissions(authState = {}) {
  const roles = []
    .concat(authState?.roles || [])
    .concat(authState?.role ? [authState.role] : [])
    .map(normalizeRole)
    .filter(Boolean);

  const permissions = new Set();
  roles.forEach((role) => {
    (ROLE_PERMISSION_MAP[role] || []).forEach((permission) => permissions.add(permission));
  });

  if (permissions.has(CORE_NETWORK_PERMISSIONS.MANAGE_SETTINGS)) {
    permissions.add(CORE_NETWORK_PERMISSIONS.SETTINGS);
  }
  if (permissions.has(CORE_NETWORK_PERMISSIONS.VIEW_DASHBOARD) || permissions.has(CORE_NETWORK_PERMISSIONS.VIEW_REQUEST) || permissions.has(CORE_NETWORK_PERMISSIONS.VIEW_MEMBERS) || permissions.has(CORE_NETWORK_PERMISSIONS.VIEW_MEMBER_DETAIL)) {
    permissions.add(CORE_NETWORK_PERMISSIONS.VIEW);
  }

  return permissions;
}

export function getCoreNetworkPermissions(authState, _instanceInput = null) {
  const permissions = new Set([
    ...getRolePermissions(authState),
    ...getExplicitPermissions(authState),
  ]);
  return [...permissions];
}

export function getCoreNetworkAccessProfile(authState, instanceInput = null) {
  const permissions = getCoreNetworkPermissions(authState, instanceInput);
  const roles = []
    .concat(authState?.roles || [])
    .concat(authState?.role ? [authState.role] : [])
    .map(normalizeRole)
    .filter(Boolean);
  const isAdministrator = roles.includes("administrator") || roles.includes("admin") || roles.includes("super-administrator");

  return {
    accessLevel: isAdministrator ? "admin" : permissions.length ? "workflow" : "none",
    visibility: isAdministrator ? "all" : permissions.includes(CORE_NETWORK_PERMISSIONS.MANAGE_PAYMENT) ? "finance" : permissions.length ? "related" : "none",
    permissions,
    instanceId: instanceInput || null,
    isAdministrator,
    isTalentPortalUser: false,
  };
}

export function hasCoreNetworkPermission(authState, permission, instanceInput = null) {
  return getCoreNetworkPermissions(authState, instanceInput).includes(permission);
}

export function hasAnyCoreNetworkPermission(authState, permissions = [], instanceInput = null) {
  return permissions.some((permission) => hasCoreNetworkPermission(authState, permission, instanceInput));
}

export function canAccessCoreNetwork(authState, instanceInput = null) {
  return hasAnyCoreNetworkPermission(
    authState,
    [
      CORE_NETWORK_PERMISSIONS.VIEW_DASHBOARD,
      CORE_NETWORK_PERMISSIONS.VIEW_REQUEST,
      CORE_NETWORK_PERMISSIONS.VIEW_MEMBERS,
      CORE_NETWORK_PERMISSIONS.VIEW_MEMBER_DETAIL,
    ],
    instanceInput
  );
}
